import { SMap, voidFunction } from '@avantia/client-and-server-utilities';
import { HpValidateGroupActionProps } from '@avantia/questionset-model';
import { recordElapsedTime } from '../clientLogging';
import { HpClientQuestionData, HpDispatchFunction, HpEventTypes, HpFieldChangedPayload } from '../types';
import { createDependentActionRunner } from './dependentActionRunner';
import { triggerFieldChangedEvent } from './eventActions';
import { HpFieldChangedDispatchFunction, HpTriggerFieldEventProps } from './eventActionTypes';

const DELAY_TIME_MS = 1500;
const delayed: SMap<NodeJS.Timeout> = {};

// eslint-disable-next-line @typescript-eslint/ban-types
export function fieldChangedAction(payload: HpFieldChangedPayload): HpFieldChangedDispatchFunction {
  return recordElapsedTime('fieldChangedAction', () => {
    const { getEvent, getQuestionSet, key, question, ui } = payload;
    let { value } = payload;
    const questionSet = getQuestionSet();
    const { displayState, questionFormats, questionSetId } = questionSet;
    const { type: eventType } = getEvent();
    const isBlur = eventType === 'blur';
    const isClick = eventType === 'click';
    const isFocus = eventType === 'focus';
    const type = isBlur ? 'FIELD_BLURRED' : 'FIELD_CHANGED';
    const unformat = payload.detail.unformat;
    if (unformat) {
      const newValue = unformat(value, ui, eventType as HpEventTypes);
      if (newValue !== value) {
        // Treat this blur event as a change event
        value = newValue;
        payload.value = value;
        payload.getEvent = getEvent;
        return fieldChangedAction(payload);
      }
    }

    const questionData = question.data as HpClientQuestionData;
    const { dependentActions, textAndStyle } = questionData;
    const { questionId } = question;
    const { questionsUI } = displayState;
    const qui = questionsUI[questionId];
    const fieldChangedProps: HpTriggerFieldEventProps = {
      key,
      question,
      questionFormats,
      getEvent,
      ui: qui,
      value
    };

    const controlType = textAndStyle.standard.controlType;
    const isFocusEvent = isFocus && controlType !== 'AutoCompleteList';
    const dependentAction = dependentActions ? dependentActions[0] : undefined;
    const changeFieldOnActionSuccess =
      dependentAction &&
      dependentAction.props &&
      (dependentAction.props as HpValidateGroupActionProps).changeFieldOnlyOnSuccess;

    if (!changeFieldOnActionSuccess || isFocusEvent) {
      triggerFieldChangedEvent(fieldChangedProps);
      if (isFocusEvent) {
        return voidFunction;
      }
    }

    if (dependentAction) {
      const dispatchFieldChanged: HpFieldChangedDispatchFunction | undefined = changeFieldOnActionSuccess
        ? (dispatch) => {
            dispatch({ type, payload });
            triggerFieldChangedEvent(fieldChangedProps);
          }
        : undefined;

      const actionFunc = createDependentActionRunner({
        dependentActions,
        question,
        payload,
        questionSet,
        questionSetId,
        dispatchAction: dispatchFieldChanged
      });
      if (actionFunc) {
        const dispatchFunc: HpFieldChangedDispatchFunction = (dispatch) => {
          return actionFunc(dispatch as HpDispatchFunction<unknown>);
        };

        return dispatchFunc;
      }
    }

    if (delayed[key]) {
      clearTimeout(delayed[key]);
    }

    const dispatchFunc: HpFieldChangedDispatchFunction = (dispatch) => {
      if (!(isBlur || isClick)) {
        delayed[key] = setTimeout(() => {
          dispatch({ type: 'DELAYED_FIELD_VALIDATION', payload });
        }, DELAY_TIME_MS);
      }

      dispatch({ type, payload });
    };

    return dispatchFunc;
  });
}
